import { useEffect, useState } from "react";
import axios from "axios";
import { useFilters } from "../context/FilterContext";
import { Database, Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Download, RefreshCw, Wifi, WifiOff, Clock, CheckCircle } from "lucide-react";

const TABS = [
  { key: "fluxx_grants", label: "Grant Details" },
  { key: "payments", label: "Payments" },
  { key: "budget_details", label: "Budget Details" },
  { key: "utilization_details", label: "Budget & Utilization" },
  { key: "outreach_details", label: "Outreach" },
  { key: "awp_details", label: "AWP" },
  { key: "meas_indicators", label: "MEAS Indicators" },
];

const PER_PAGE_OPTIONS = [25, 50, 100];

export default function RawData() {
  const { API, filterOptions } = useFilters();
  const [activeTab, setActiveTab] = useState("fluxx_grants");
  const [tableMeta, setTableMeta] = useState({});
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [sfName, setSfName] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const sfConnected = filterOptions.data_available;
  const cacheStatus = filterOptions.cache_status || {};
  const lastSync = cacheStatus.last_synced_at;


  const downloadTable = async (tableKey, label) => {
    const response = await axios.get(`${API}/raw-data/${tableKey}/export`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${tableKey}_export.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      for (const tab of TABS) {
        await downloadTable(tab.key, tab.label);
      }
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${API}/etl/refresh`, {}, { withCredentials: true });
      setSyncResult({ success: true, counts: res.data.counts });
      // Refresh table data after sync
      setPage(1);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setSyncResult({ success: false, error: detail });
    } finally {
      setSyncing(false);
    }
  };

  // Fetch table metadata on mount
  useEffect(() => {
    axios.get(`${API}/raw-data/tables`).then(r => {
      const meta = {};
      r.data.forEach(t => { meta[t.key] = t; });
      setTableMeta(meta);
    }).catch(console.error);
  }, [API]);

  // Fetch rows when tab/page/sort/search changes
  useEffect(() => {
    setLoading(true);
    const params = { page, per_page: perPage };
    if (search) params.search = search;
    if (sortCol) { params.sort_col = sortCol; params.sort_dir = sortDir; }
    axios.get(`${API}/raw-data/${activeTab}`, { params })
      .then(r => {
        setData(r.data.data || []);
        setColumns(r.data.columns || []);
        setSfName(r.data.sf_name || "");
        setTotal(r.data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [API, activeTab, page, perPage, search, sortCol, sortDir]);

  // Reset page when switching tabs or searching
  useEffect(() => { setPage(1); setSortCol(null); setSortDir("asc"); setSearch(""); setSearchInput(""); }, [activeTab]);

  const totalPages = Math.ceil(total / perPage);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const fmtHeader = (col) => col.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  const fmtCell = (val) => {
    if (val === null || val === undefined) return <span className="text-slate-500">—</span>;
    if (typeof val === "number") {
      if (Math.abs(val) >= 1e5) return val.toLocaleString("en-IN", { maximumFractionDigits: 2 });
      return String(val);
    }
    const s = String(val);
    if (s.length > 60) return <span title={s}>{s.slice(0, 57)}...</span>;
    return s;
  };

  const meta = tableMeta[activeTab];

  return (
    <div className="space-y-3" data-testid="raw-data-page">
      {/* Data Source Status & Sync Panel */}
      <div className="rounded-lg border border-slate-200 bg-white" data-testid="data-source-status-panel">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            {/* Connection Status */}
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sfConnected ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`} data-testid="sf-connection-status">
              {sfConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
              <span>{sfConnected ? "Data Source Connected" : "Data Source Disconnected"}</span>
            </div>

            {/* Last Synced */}
            <div className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="last-sync-time">
              <CheckCircle size={12} className={lastSync ? "text-blue-500" : "text-slate-500"} />
              <span>{lastSync ? `Last refreshed: ${new Date(lastSync).toLocaleString()}` : "No refresh yet"}</span>
            </div>
          </div>

          {/* Sync Now Button */}
          <button
            data-testid="sync-now-btn"
            onClick={handleSyncNow}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#006837] text-white rounded-lg text-xs font-medium hover:bg-[#9a1717] disabled:opacity-60 transition-colors whitespace-nowrap"
          >
            <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing..." : "Sync Now"}
          </button>
        </div>

        {/* Sync Result Message */}
        {syncResult && (
          <div className={`px-4 py-2 border-t text-xs ${syncResult.success ? "bg-green-50 text-green-700 border-green-100" : "bg-red-50 text-red-700 border-red-100"}`} data-testid="sync-result">
            {syncResult.success
              ? `Sync complete. Rows updated: ${Object.entries(syncResult.counts || {}).map(([k, v]) => `${k}: ${v}`).join(", ")}`
              : `Sync failed: ${syncResult.error}`
            }
          </div>
        )}
      </div>

      {/* Tabs + Download All */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-0" data-testid="raw-data-tabs">
        <div className="flex items-center gap-1">
          {TABS.map(tab => {
            const m = tableMeta[tab.key];
            return (
              <button
                key={tab.key}
                data-testid={`raw-tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-all ${
                  activeTab === tab.key
                    ? "border-[#006837] text-[#006837]"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                }`}
              >
                {tab.label}
                {m && (
                  <span className="ml-1.5 text-[10px] text-slate-600 font-normal">
                    ({m.total_rows.toLocaleString()})
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          data-testid="download-all-tables-btn"
          onClick={handleDownloadAll}
          disabled={downloading}
          className="flex items-center gap-1.5 px-3 py-1.5 mb-1 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-60 transition-colors whitespace-nowrap"
        >
          <Download size={13} />
          {downloading ? "Downloading..." : "Download All Excel Tables"}
        </button>
      </div>

      {/* Table Header + Search */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database size={14} className="text-[#006837]" />
          <span className="text-sm font-semibold text-slate-800">{sfName}</span>
          {meta && (
            <span className="text-xs text-slate-600">
              ({meta.pg_table} &middot; {total.toLocaleString()} rows)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-1">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-600" />
              <input
                data-testid="raw-data-search"
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search..."
                className="pl-7 pr-2 py-1 text-xs border border-slate-200 rounded-md w-48 focus:outline-none focus:ring-1 focus:ring-[#006837]/30 focus:border-[#006837]/50"
              />
            </div>
            <button type="submit" className="px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-md transition-colors">Go</button>
          </form>
          <select
            data-testid="raw-data-per-page"
            value={perPage}
            onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
            className="text-xs border border-slate-200 rounded-md px-1.5 py-1 focus:outline-none"
          >
            {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
        </div>
      </div>

      {/* Data Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-260px)]">
          <table className="w-full text-xs" data-testid="raw-data-table">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500 border-b border-slate-200 w-10">#</th>
                {columns.map(col => (
                  <th
                    key={col}
                    className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500 border-b border-slate-200 cursor-pointer hover:bg-slate-100 select-none whitespace-nowrap"
                    onClick={() => handleSort(col)}
                    data-testid={`raw-sort-${col}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {fmtHeader(col)}
                      {sortCol === col ? (
                        sortDir === "asc" ? <ArrowUp size={10} className="text-[#006837]" /> : <ArrowDown size={10} className="text-[#006837]" />
                      ) : (
                        <ArrowUpDown size={9} className="text-slate-500" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 1} className="text-center py-12 text-slate-600">Loading...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="text-center py-12 text-slate-600">No data found</td></tr>
              ) : (
                data.map((row, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`raw-row-${idx}`}>
                    <td className="px-2 py-1.5 text-[10px] text-slate-600">{(page - 1) * perPage + idx + 1}</td>
                    {columns.map(col => (
                      <td key={col} className="px-2 py-1.5 whitespace-nowrap max-w-[200px] overflow-hidden text-ellipsis">
                        {fmtCell(row[col])}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-slate-500" data-testid="raw-data-pagination">
        <span>
          Showing {data.length > 0 ? (page - 1) * perPage + 1 : 0}–{Math.min(page * perPage, total)} of {total.toLocaleString()}
        </span>
        <div className="flex items-center gap-1">
          <button
            data-testid="raw-page-prev"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={14} />
          </button>
          {/* Page numbers */}
          {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
            let p;
            if (totalPages <= 7) {
              p = i + 1;
            } else if (page <= 4) {
              p = i + 1;
            } else if (page >= totalPages - 3) {
              p = totalPages - 6 + i;
            } else {
              p = page - 3 + i;
            }
            return (
              <button
                key={p}
                data-testid={`raw-page-${p}`}
                onClick={() => setPage(p)}
                className={`w-6 h-6 rounded text-[11px] font-medium transition-colors ${
                  p === page ? "bg-[#006837] text-white" : "hover:bg-slate-100 text-slate-600"
                }`}
              >
                {p}
              </button>
            );
          })}
          <button
            data-testid="raw-page-next"
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
